// frontend/packages/business-console/src/components/content/FileList.jsx
//
// The Content hub's page inventory (left rail). Token-kit styling to match
// the rest of the console: light grouped lists (collection caption + rows)
// token colors instead of MUI variants / opacity. The page title + stats
// live in the hub header above, so this rail no longer repeats a "Content"
// heading.
//
// Each collection is a collapsible group, collapsed by default (the header
// shows the page count); the group holding the open file auto-expands. Each
// row shows the file name + its per-page performance (views/leads/SEO,
// joined via the heuristic path map) and a "Modified" pill for unpublished
// edits.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

import {
  stripMarkdownExtension,
  humanizeLabel,
  SINGLETON_COLLECTIONS,
  contentSitePath,
} from '../../utils/contentEditorHelpers';
import { StatusPill, useTokens } from '../primitives';

// Compact per-file performance line. Joins the file (via its heuristic site
// path) to the page metrics; shows a plain, honest "No traffic yet" when the
// page has no recorded views/leads (or the file did not resolve to a path).
function fileMetricLine(file, metricsByPath) {
  const m = metricsByPath ? metricsByPath[contentSitePath(file)] : null;
  if (!m) return 'No traffic yet';
  const parts = [
    `${(m.views || 0).toLocaleString()} views`,
    `${m.leads || 0} leads`,
  ];
  if (typeof m.onPageScore === 'number') parts.push(`SEO ${m.onPageScore}`);
  return parts.join(' · ');
}

const safe = (v) => (v == null ? '' : String(v));

// A collection with more entries than this starts collapsed (see below).
const LARGE_COLLECTION = 25;

export default function FileList({
  businessId,
  files,
  metricsByPath,
  selectedPath,
  loading,
  error,
  onRefresh,
  onOpenFile,
  onCreateInCollection,
}) {
  const t = useTokens();

  const filesByCollection = useMemo(() => {
    const grouped = {};
    for (const f of files || []) {
      const collection = f?.collection || 'other';
      (grouped[collection] ||= []).push(f);
    }
    for (const key of Object.keys(grouped)) {
      grouped[key] = grouped[key]
        .slice()
        .sort((a, b) => safe(a?.path).localeCompare(safe(b?.path)));
    }
    return grouped;
  }, [files]);

  const collections = useMemo(
    () => Object.keys(filesByCollection).sort(),
    [filesByCollection]
  );

  // Seed the open set once the list arrives, without clobbering a collection
  // the operator has since toggled: only keys we have never recorded are set.
  useEffect(() => {
    if (collections.length === 0) return;
    setOpenCollections((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const collection of collections) {
        if (Object.prototype.hasOwnProperty.call(next, collection)) continue;
        next[collection] =
          (filesByCollection[collection] || []).length <= LARGE_COLLECTION;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [collections, filesByCollection]);

  // Collections open by default, so the Content hub's first paint shows the
  // operator their actual pages instead of a column of empty group headers next
  // to a "choose something to edit" pane. A collection past LARGE_COLLECTION
  // stays collapsed: a site with hundreds of posts should not bury the other
  // groups under one of them. Explicit toggles always win afterwards.
  const [openCollections, setOpenCollections] = useState({});
  const toggleCollection = (collection) =>
    setOpenCollections((prev) => ({
      ...prev,
      [collection]: !prev[collection],
    }));

  // Keep the selected file's collection open so opening a page never leaves it
  // hidden behind a collapsed group.
  const selectedCollection = useMemo(() => {
    if (!selectedPath) return '';
    const f = (files || []).find((x) => x?.path === selectedPath);
    return f?.collection || '';
  }, [files, selectedPath]);

  useEffect(() => {
    if (!selectedCollection) return;
    setOpenCollections((prev) =>
      prev[selectedCollection] ? prev : { ...prev, [selectedCollection]: true }
    );
  }, [selectedCollection]);

  return (
    <Box>
      <Box
        sx={{
          p: '12px 14px',
          position: { md: 'sticky' },
          top: 0,
          zIndex: 1,
          bgcolor: t.card,
          borderBottom: `1px solid ${t.hairline}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              flex: 1,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '.6px',
              textTransform: 'uppercase',
              color: t.faint,
            }}
          >
            Pages
          </Typography>
          <IconButton
            size="small"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Refresh"
          >
            <RefreshIcon sx={{ fontSize: 18, color: t.faint }} />
          </IconButton>
        </Box>

        {!businessId ? (
          <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
            No business is associated with your account.
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box
            sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" height={40} />
            ))}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ p: 1 }}>
        {collections.length === 0 && !loading ? (
          <Typography sx={{ fontSize: 12.5, color: t.muted, p: 1 }}>
            No content files found.
          </Typography>
        ) : null}

        {collections.map((collection) => {
          const items = filesByCollection[collection] || [];
          const isSingleton = SINGLETON_COLLECTIONS.has(collection);
          const open = Boolean(openCollections[collection]);
          return (
            <Box key={collection} sx={{ mb: 0.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pr: 1.25,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => toggleCollection(collection)}
                  aria-expanded={open}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    font: 'inherit',
                    border: 'none',
                    bgcolor: 'transparent',
                    textAlign: 'left',
                    px: 0.75,
                    py: 1,
                    borderRadius: '8px',
                    '&:hover': { bgcolor: t.subtle },
                  }}
                >
                  <KeyboardArrowRightRoundedIcon
                    sx={{
                      fontSize: 18,
                      color: t.faint,
                      transform: open ? 'rotate(90deg)' : 'none',
                      transition: 'transform .15s ease',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: '.6px',
                      textTransform: 'uppercase',
                      color: t.faint,
                    }}
                  >
                    {humanizeLabel(collection)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: t.faint,
                      opacity: 0.7,
                    }}
                  >
                    {items.length}
                  </Typography>
                </Box>
                {!isSingleton ? (
                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onCreateInCollection(collection)}
                    sx={{
                      minWidth: 0,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'none',
                      color: t.blue,
                    }}
                  >
                    New
                  </Button>
                ) : null}
              </Box>

              <Collapse in={open} unmountOnExit>
                <Box sx={{ pt: 0.25 }}>
                  {items.map((f) => {
                    const path = f?.path || '';
                    const rawName = stripMarkdownExtension(
                      f?.name || path.split('/').pop() || 'Untitled'
                    );
                    const name = humanizeLabel(rawName);
                    const active = selectedPath && path === selectedPath;
                    const modified = Boolean(f?.modifiedSincePublish);
                    return (
                      <Box
                        key={f?.id || path || name}
                        component="button"
                        onClick={() => onOpenFile(f)}
                        sx={{
                          width: '100%',
                          textAlign: 'left',
                          cursor: 'pointer',
                          font: 'inherit',
                          border: 'none',
                          display: 'block',
                          px: 1.25,
                          py: '7px',
                          mb: '2px',
                          borderRadius: '8px',
                          bgcolor: active ? t.blueBg : 'transparent',
                          transition: 'background-color .12s ease',
                          '&:hover': { bgcolor: active ? t.blueBg : t.subtle },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography
                            noWrap
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: 13,
                              fontWeight: 600,
                              color: active
                                ? t.blue
                                : modified
                                  ? t.amberText
                                  : t.text,
                            }}
                          >
                            {name}
                          </Typography>
                          {modified ? (
                            <StatusPill
                              size="sm"
                              kind="warning"
                              label="Modified"
                            />
                          ) : null}
                        </Box>
                        <Typography
                          noWrap
                          sx={{ fontSize: 11, color: t.faint, mt: '1px' }}
                        >
                          {fileMetricLine(f, metricsByPath)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
